import { useMemo } from "react"

import { Logger } from "shared/lib/Logger"
import { useAuth } from "shared/lib/useAuth"
import { InvalidDataError } from "shared/types/error/InvalidDataError"

import { useProjectData } from "entities/projects/api/useProjectData"
import { sortTeamMembers } from "entities/team-members/model/sortTeamMembers"
import { useTeamMemberByEmail } from "entities/team-members/model/useTeamMemberByEmail"
import { useTeamQuery } from "entities/teams/graphql/queries.generated"

interface UseProjectTeamDataArgs {
  projectID: string
}

export function useProjectTeamData({ projectID }: UseProjectTeamDataArgs) {
  const { userInfo } = useAuth()
  const { project, isLoading: isLoadingProject } = useProjectData(projectID)
  const { data: teamData, loading: isLoadingTeam } = useTeamQuery({
    variables: { id: project?.team?.id as string },
    skip: !project?.team?.id
  })

  const team = teamData?.team

  const currentUserMember = useTeamMemberByEmail({
    teamMembers: team?.memberships,
    email: userInfo?.email
  })

  const sortedTeam = useMemo(() => {
    if (!team) return team
    if (!team.memberships) {
      Logger.error(
        new InvalidDataError("Team is missing memberships", { data: team })
      )
    }
    return {
      ...team,
      memberships: sortTeamMembers(team.memberships || [], currentUserMember)
    }
  }, [team, currentUserMember])

  return {
    team: sortedTeam,
    isLoading: isLoadingProject || isLoadingTeam
  }
}
